/**
 * Entity Registry — single source of truth for "where does this entity type live?".
 *
 * Every entity type has exactly one canonical home, decided here. Consumers
 * never touch backing stores directly; they go through `entityRouter` hooks
 * which call into this registry to resolve the right store.
 *
 * Scopes:
 *   - 'space'        → spaceEntityStore (per-space LRU=8 + IDB), keyed by activeSpaceId.
 *   - 'global'       → globalEntityStore (cross-space, eager, IDB-backed).
 *   - 'mail'         → mailAppStore       (self-managed via mailCrdtBridge).
 *   - 'tasks'        → tasksAppStore      (self-managed).
 *   - 'contacts'     → contactsAppStore   (self-managed).
 *   - 'bank'         → bankAppStore       (self-managed).
 *   - 'automations'  → automationsStore   (self-managed).
 *   - 'database'     → databaseAppStore   (self-managed).
 *
 * Adding a new entity type? Register it here. Forgetting to register is a
 * silent default to 'space' — the unit test `entityRegistry.test.ts` is the
 * canonical place to assert known types map to the expected scope.
 *
 * The registry exposes both selectors and dispatchers so the live-update bus
 * (`orgYDocStore`) and system-command code paths can route changes uniformly.
 */

import type { BaseEntityType } from '../types/entities';

export type EntityScope =
  | 'space'
  | 'global'
  | 'mail'
  | 'tasks'
  | 'contacts'
  | 'bank'
  | 'automations'
  | 'database';

/** Exact-match table for non-prefix entity types. */
const EXACT_SCOPES: Readonly<Record<string, EntityScope>> = Object.freeze({
  // Global — cross-space, surfaced everywhere.
  Space: 'global',
  Org: 'global',
  MCPConnection: 'global',
  APIConnection: 'global',
  ManagedAPI: 'global',
  // Legacy "Integration" base type without dot — global as well.
  Integration: 'global',

  // Mail — owned by mailAppStore.
  'Mail.Thread': 'mail',
  'Mail.Folder': 'mail',
  'Mail.Draft': 'mail',
  'Mail.Message': 'mail',
  'Mail.Account': 'mail',
  'Mail.Contact': 'mail',

  // Tasks — owned by tasksAppStore.
  Task: 'tasks',
  Area: 'tasks',
  'Tasks.Task': 'tasks',
  'Tasks.Area': 'tasks',
  'Tasks.Project': 'tasks',
  'Tasks.List': 'tasks',

  // Contacts — owned by contactsAppStore.
  Contact: 'contacts',

  // Bank — owned by bankAppStore.
  BankAccount: 'bank',
  BankTransaction: 'bank',
  // Legacy Plaid* names predate the bank rename; route them too.
  PlaidItem: 'bank',
  PlaidAccount: 'bank',
  PlaidTransaction: 'bank',

  // Automations — owned by automationsStore.
  Automation: 'automations',
  'Automation.Run': 'automations',
  Job: 'automations',
  Schedule: 'automations',

  // Database — owned by databaseAppStore.
  Table: 'database',
  TableRow: 'database',
  'Database.Table': 'database',
  'Database.Connection': 'database',
  'Database.Row': 'database',
});

/** Prefix rules applied when no exact match is found. Order matters. */
const PREFIX_SCOPES: ReadonlyArray<readonly [string, EntityScope]> = Object.freeze([
  ['Integration.', 'global'],
  ['Mail.', 'mail'],
  ['Tasks.', 'tasks'],
  ['Bank.', 'bank'],
  ['Database.', 'database'],
  ['Automation.', 'automations'],
]);

/** Resolve an entity type to its canonical store scope. Unknown → 'space'. */
export function resolveScope(entityType: string | undefined | null): EntityScope {
  if (!entityType) return 'space';
  const exact = EXACT_SCOPES[entityType];
  if (exact) return exact;
  for (const [prefix, scope] of PREFIX_SCOPES) {
    if (entityType.startsWith(prefix)) return scope;
  }
  return 'space';
}

// ─────────────────────────────────────────────────────────────────────────────
// Registry adapters — each scope provides four operations.
// Implementations are wired up at module-init time by the corresponding store
// (spaceEntityStore, globalEntityStore, mailAppStore, etc.) so we avoid
// circular imports.
// ─────────────────────────────────────────────────────────────────────────────

export interface ScopeAdapter {
  /** Return the entity by id if the scope holds it, otherwise undefined. */
  getById: (id: string) => BaseEntityType | undefined;
  /** Return all entities of the given exact entityType this scope holds. */
  getByType: (entityType: string) => BaseEntityType[];
  /** Insert or update an entity in the scope's backing store. */
  upsert: (entity: BaseEntityType) => void;
  /** Remove an entity by id from the scope's backing store. */
  deleteEntity: (id: string) => void;
  /** Optional dump for debugging / cross-scope enumeration. */
  dumpAll?: () => BaseEntityType[];
}

const _adapters: Partial<Record<EntityScope, ScopeAdapter>> = {};

/**
 * Install an adapter for a given scope. Called once per scope at app boot
 * (typically at the bottom of the corresponding store module).
 *
 * Idempotent: re-registering a scope replaces the previous adapter, which is
 * the right behavior when stores reset on org switch / HMR.
 */
export function registerScopeAdapter(scope: EntityScope, adapter: ScopeAdapter): void {
  _adapters[scope] = adapter;
}

/** Internal — used by tests and the dump helper. */
export function _allAdapters(): Array<[EntityScope, ScopeAdapter]> {
  return Object.entries(_adapters) as Array<[EntityScope, ScopeAdapter]>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public registry surface — non-hook accessors for system commands and the
// live-update bus. React components should prefer the hooks in entityRouter.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Look up an entity by id across all registered scopes.
 *
 * Order: space → global → mail → tasks → contacts → bank → automations → database.
 * The first scope that owns the id wins; other scopes are not consulted.
 */
export function getById(id: string): BaseEntityType | undefined {
  if (!id) return undefined;
  for (const scope of ['space', 'global', 'mail', 'tasks', 'contacts', 'bank', 'automations', 'database'] as EntityScope[]) {
    const adapter = _adapters[scope];
    if (!adapter) continue;
    const found = adapter.getById(id);
    if (found) return found;
  }
  return undefined;
}

/**
 * Return all entities of the given exact entityType across the owning scope.
 * Same adapter-missing fallback as `upsert`: if the resolved scope has no
 * adapter, fall through to the `space` adapter so app-store-owned types that
 * currently live in `spaceEntityStore` still resolve.
 */
export function getByType(entityType: string): BaseEntityType[] {
  const scope = resolveScope(entityType);
  const adapter = _adapters[scope] ?? _adapters['space'];
  if (!adapter) return [];
  return adapter.getByType(entityType);
}

/**
 * Dispatch an upsert through the registry. The owning scope is determined by
 * the entity's `entityType` field.
 *
 * Adapter-missing fallback: when the resolved scope has no adapter (e.g. the
 * dedicated app store for `Automation` / `Task` / `Bank.*` / `Contact` /
 * `Database.*` hasn't migrated to register an adapter yet), we fall through
 * to the `space` adapter. This is the canonical interim home for those
 * entities — `automationsStore`, `tasksAppStore`, etc. consume them from
 * `spaceEntityStore` today. Once a dedicated adapter is registered, it wins
 * over the fallback automatically.
 */
export function upsert(entity: BaseEntityType): void {
  if (!entity || !entity.entityType) return;
  const scope = resolveScope(entity.entityType);
  const adapter = _adapters[scope] ?? _adapters['space'];
  adapter?.upsert(entity);
}

/**
 * Dispatch a delete through the registry. When `entityType` is known, dispatch
 * to the owning scope only (with the same `space` adapter fallback as
 * `upsert`); otherwise fan out to every registered scope (for Y.Doc
 * tombstones, where the deleted node's type is no longer readable).
 */
export function deleteEntity(id: string, entityType?: string): void {
  if (!id) return;
  if (entityType) {
    const adapter = _adapters[resolveScope(entityType)] ?? _adapters['space'];
    adapter?.deleteEntity(id);
    return;
  }
  for (const adapter of Object.values(_adapters)) {
    adapter?.deleteEntity(id);
  }
}

/** Enumerate every entity across every scope (for debug / dev console). */
export function dumpAll(): Array<{ scope: EntityScope; entities: BaseEntityType[] }> {
  return _allAdapters().map(([scope, adapter]) => ({
    scope,
    entities: adapter.dumpAll?.() ?? [],
  }));
}

/**
 * Subscribe to "anything in the registry has changed" events. Used by app
 * stores (mail / tasks / bank / …) that listen for upstream entity mutations
 * to keep their derived caches in sync.
 *
 * Returns an unsubscribe function. The callback is invoked once per upstream
 * `bumpVersion` tick — internally we listen to both `useSpaceEntityStore` and
 * `useGlobalEntityStore`. Other adapter-owning stores (mail/tasks/…) call
 * `notifyChange()` directly when their own state mutates.
 */
const _changeListeners = new Set<() => void>();

export function subscribeToChanges(callback: () => void): () => void {
  _changeListeners.add(callback);
  return () => {
    _changeListeners.delete(callback);
  };
}

/**
 * Internal — called by adapters (or the registry's upsert/delete dispatchers)
 * whenever the registry's overall state changes. Most call sites won't need
 * this; the space and global stores wire it up at module init time.
 */
export function notifyChange(): void {
  for (const listener of _changeListeners) {
    try {
      listener();
    } catch (err) {
      // Listeners must not throw — if they do, isolate so other listeners run.
      console.error('[entityRegistry] subscribeToChanges listener threw:', err);
    }
  }
}

/** Convenience namespace export so callers can write `entityRegistry.getById(id)`. */
export const entityRegistry = {
  resolveScope,
  registerScopeAdapter,
  getById,
  getByType,
  upsert,
  deleteEntity,
  dumpAll,
  subscribeToChanges,
  notifyChange,
};
